/**
 * Officer-card types and helpers shared by OfficerEditPopup (the actual popup
 * UI lives inline there) and the Officers tab: the AssignmentRow/OfficerCard
 * shapes, deriveAssignments, and the small time-math helpers below.
 */

import {
  hhmmToMinutes,
  hmmmToSlot,
  hmmmToEndSlot,
} from '@/config/modeConfig';
import { slotToHhmm } from '@/utils/partialTiming';
import type { RosterGridData, OfficerMetadata } from '@/types/api';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface AssignmentRow {
  id: string;
  startTime: string;
  endTime: string;
  counter: string; // 1-based counter number as string, e.g. "3"; "0" on a free-text duty row
  /** A free-text duty ("SOS DCAR", "B@M CTRL BOOTH") instead of a counter: working time off the
   *  counters (RosterState.duty_slots). The counter is then "0". */
  duty?: string;
  /** Short caption shown on the grid cell for this segment (top-middle, same line as
   *  the HHMM labels) — editable here, rendered on the canvas like a tiktok caption. */
  notes?: string;
}

export interface OfficerCard {
  officerId: string;
  metadata: OfficerMetadata;
  assignments: AssignmentRow[];
  isNew?: boolean; // true for locally-created cards not yet persisted to backend
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function rowId() {
  return Math.random().toString(36).slice(2);
}

/** Signed minutes between two HHMM strings. Negative = overlap. Handles midnight wraparound. */
export function minutesBetween(endTime: string, startTime: string): number {
  let diff = hhmmToMinutes(startTime) - hhmmToMinutes(endTime);
  // Night-shift midnight wraparound: e.g. end=23:30 → start=00:00 should be +30, not -1410
  if (diff < -720) diff += 1440;
  return diff;
}

/** Duration in minutes between two HHMM strings. */
export function calcDurationMinutes(start: string, end: string, startHour: number): number {
  const slots = hmmmToEndSlot(end, startHour) - hmmmToSlot(start, startHour);
  return Math.max(15, slots * 15);
}

/** Adds 15 minutes to an HHMM string. */
export function addQuarterHour(hhmm: string): string {
  const h = parseInt(hhmm.slice(0, 2), 10);
  const m = parseInt(hhmm.slice(2, 4), 10);
  const total = h * 60 + m + 15;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}${String(total % 60).padStart(2, '0')}`;
}

/**
 * Scan RosterGridData cells for a given officer and collapse consecutive
 * time-slots on the same counter into AssignmentRow objects.
 */
export function deriveAssignments(officerId: string, gridData: RosterGridData): AssignmentRow[] {
  const { cells, time_labels } = gridData;

  const hits = cells
    .filter((c) => c.officers.includes(officerId))
    .map((c) => ({ col: c.col, counterLabel: String(c.counter ?? (c.row + 1)), note: c.note }))
    .sort((a, b) => a.col - b.col);

  const duties = dutyRows(officerId, gridData);
  if (!hits.length) return duties;

  const rows: AssignmentRow[] = [];
  let segStart = hits[0].col;
  let segEnd = hits[0].col;
  let segCounter = hits[0].counterLabel;
  let segNote = hits[0].note;

  for (let i = 1; i < hits.length; i++) {
    const { col, counterLabel, note } = hits[i];
    // A run whose note changes mid-way must not merge into one row that only
    // shows the first slot's note — split it here just like a counter change.
    if (counterLabel === segCounter && note === segNote && col === segEnd + 1) {
      segEnd = col;
    } else {
      rows.push({
        id: rowId(),
        startTime: time_labels[segStart] ?? '',
        endTime: time_labels[segEnd + 1] ?? addQuarterHour(time_labels[segEnd] ?? '0000'),
        counter: segCounter,
        notes: segNote,
      });
      segStart = col;
      segEnd = col;
      segCounter = counterLabel;
      segNote = note;
    }
  }
  rows.push({
    id: rowId(),
    startTime: time_labels[segStart] ?? '',
    endTime: time_labels[segEnd + 1] ?? addQuarterHour(time_labels[segEnd] ?? '0000'),
    counter: segCounter,
    notes: segNote,
  });

  // A duty is a row like any other, in time order with the counters around it.
  return [...rows, ...duties].sort((a, b) => time_labels.indexOf(a.startTime) - time_labels.indexOf(b.startTime));
}

/** One row per run of slots the officer spends on the same free-text duty. */
function dutyRows(officerId: string, gridData: RosterGridData): AssignmentRow[] {
  const { time_labels } = gridData;
  const slots = Object.entries(gridData.duty_slots?.[officerId] ?? {})
    .map(([slot, text]) => ({ slot: Number(slot), text }))
    .filter((d) => Number.isInteger(d.slot))
    .sort((a, b) => a.slot - b.slot);
  const out: AssignmentRow[] = [];
  let k = 0;
  while (k < slots.length) {
    let j = k;
    while (j + 1 < slots.length && slots[j + 1].slot === slots[j].slot + 1 && slots[j + 1].text === slots[k].text) j++;
    out.push({
      id: rowId(),
      startTime: time_labels[slots[k].slot] ?? '',
      endTime: time_labels[slots[j].slot + 1] ?? addQuarterHour(time_labels[slots[j].slot] ?? '0000'),
      counter: '0',
      duty: slots[k].text,
    });
    k = j + 1;
  }
  return out;
}

/**
 * Splits any assignment row that straddles an availability-window boundary into
 * two rows at that boundary, each keeping the original counter. `deriveAssignments`
 * reads only grid cells, which know nothing about availability windows, so a
 * continuous counter run can span a boundary a window-split created afterwards —
 * left unsplit, the popup buckets the whole run under whichever window its start
 * falls in, leaving the other window's slice of it with no counter row at all.
 */
export function splitAssignmentsAtBoundaries(
  assignments: AssignmentRow[],
  segs: Array<{ start: string; end: string }>,
  startHour: number,
): AssignmentRow[] {
  if (segs.length < 2) return assignments;
  const boundarySlots = new Set<number>();
  for (const seg of segs) {
    try {
      boundarySlots.add(hmmmToSlot(seg.start, startHour));
      boundarySlots.add(hmmmToEndSlot(seg.end, startHour));
    } catch { /* skip malformed segment */ }
  }
  const boundaries = Array.from(boundarySlots).sort((a, b) => a - b);

  const result: AssignmentRow[] = [];
  for (const a of assignments) {
    let s: number, e: number;
    try { s = hmmmToSlot(a.startTime, startHour); e = hmmmToEndSlot(a.endTime, startHour); }
    catch { result.push(a); continue; }
    const cuts = boundaries.filter((b) => b > s && b < e);
    if (cuts.length === 0) { result.push(a); continue; }
    let pieceStart = s;
    for (const cut of [...cuts, e]) {
      result.push({
        ...a,
        id: pieceStart === s ? a.id : rowId(),
        startTime: slotToHhmm(pieceStart, startHour),
        endTime: slotToHhmm(cut, startHour),
      });
      pieceStart = cut;
    }
  }
  return result;
}
